import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  GenTextField,
  GenLayout,
  GenDropDown,
  GenButton,
  GenAutoComplete,
  GenDatePicker,
} from "@ui";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { AnyObjectSchema } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  API_STATUS,
  AppResponse,
  Country,
  CoveragePlan,
  District,
  Option,
  PostalCode,
  PromocodeRequest,
  PromoCodeResponse,
  PromoItem,
  Province,
  SubDistrict,
  WebFormByPromocodeResponse,
  WebFormByRequesterRequest,
  WebFormByRequesterResponse,
  WebFormRequest,
  WebFormResponse,
  WebFormType,
} from "@types";
import {
  beneficiary,
  countries,
  nationality,
  postalCode as allPostalCodes,
  districts as allDistricts,
  subDistricts as allSubDistricts,
  provinces,
  title,
} from "@data";
import {
  addDays,
  differenceInCalendarDays,
  subMonths,
  subYears,
} from "date-fns";
import { SECONDARY_COLOR_LIGHT } from "@ui/themes/gen-theme";
import { postGraphql, calculateAgeInYearsMonths } from "@libs/utils";
import { FormHelperText } from "@mui/material";
import { createWebForm } from "@graphql/mutations";
import {
  getPromocode,
  listWebFormByPromocode,
  listWebFormByRequester,
} from "@graphql/queries";

interface FormType {
  destination: Country;
  departureDate: Date | null;
  returnDate: Date | null;
  travelDays: number;
  nationality: Option;
  title: Option;
  firstNameTH?: string;
  lastNameTH?: string;
  firstName: string;
  lastName: string;
  dob: Date | null;
  idCardNo?: string;
  passportNo?: string;
  phoneNumber: string;
  email: string;
  beneficiary: Option;
  beneficiaryTitle?: Option;
  beneficiaryFirstName?: string;
  beneficiaryLastName?: string;
  insuredAddress: string;
  street?: string;
  province: Province;
  district: District;
  subDistrict: SubDistrict;
  postalCode: PostalCode;
  promoCode: string;
  promoType?: string;
  termConditionFlag: boolean;
  marketingConsentFlag?: boolean;
}

interface DeleteUserProps {
  onChangeStep: (step: number) => void;
}

const checkThai = new RegExp(/[\u0E00-\u0E7F]$/);
const checkEng = new RegExp(/[a-zA-Z]$/);
const checkNoSpecialChar = new RegExp(/[a-zA-Z0-9]$/);

// const calculateAgeinYearsMonths = (dob: Date): string => {
//   const now = new Date();
//   const currentYear = now.getFullYear();
//   const currentMonth = now.getMonth() + 1;

//   const birthYear = dob.getFullYear();
//   const birthMonth = dob.getMonth() + 1;

//   let years = currentYear - birthYear;
//   let months = currentMonth - birthMonth;

//   if (months < 0) {
//     years -= 1;
//     months += 12;
//   }

//   return `${years} y ${months} m`;
// };

const GiveAway: FC<DeleteUserProps> = (props) => {
  const { onChangeStep } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [districts, setDistricts] = useState<District[]>([]);
  const [subDistricts, setSubDistricts] = useState<SubDistrict[]>([]);
  const [postalCodes, setPostalCodes] = useState<PostalCode[]>([]);
  const [promoCodeValidError, setPromoCodeValidError] = useState("");
  const formRef = useRef<HTMLFormElement | null>(null);
  const [refresh, setRefresh] = useState(false);

  const schema: AnyObjectSchema = yup.object().shape({
    destination: yup
      .object({
        id: yup.string(),
        label_th: yup.string(),
        label_en: yup.string(),
      })
      .test(
        "check valid",
        "error_field.destination_error",
        (value) => value?.id !== undefined,
      )
      .required("error_field.destination_error"),
    departureDate: yup
      .date()
      .nullable()
      .required("error_field.departure_date_error")
      .test(
        "is-valid-date",
        "error_field.departure_date_invalid_error",
        (value) => {
          return (
            value !== null &&
            value >= addDays(new Date(), 6) &&
            value <= addDays(new Date(), 180)
          );
        },
      ),
    returnDate: yup.lazy(() =>
      yup
        .date()
        .nullable()
        .required("error_field.return_date_error")
        .when(["departureDate", "returnDate"], {
          is: (departureDate: Date | null, returnDate: Date | null) =>
            departureDate !== null &&
            returnDate !== null &&
            departureDate <= returnDate &&
            returnDate <= addDays(departureDate, 6),
          then: (
            schema, // throw error if not in valid range
          ) =>
            schema.test(
              "is-valid-age",
              "error_field.return_date_invalid_error",
              () => {
                return true;
              },
            ),
          otherwise: (schema) =>
            schema.test(
              "is-valid-age",
              "error_field.return_date_invalid_error",
              () => {
                return false;
              },
            ),
        }),
    ),
    travelDays: yup.number().max(7).required(""),
    nationality: yup
      .object({
        id: yup.string().required("error_field.nationality_error"),
        label_th: yup.string(),
        label_en: yup.string(),
      })
      .required("error_field.nationality_error"),
    title: yup
      .object({
        id: yup.string().required("error_field.title_error"),
        label_th: yup.string(),
        label_en: yup.string(),
      })
      .required("error_field.title_error"),
    firstName: yup
      .string()
      .max(30)
      .required("error_field.first_name_error")
      .matches(checkEng, {
        message: "error_field.language_en_error",
        excludeEmptyString: true,
      }),
    lastName: yup
      .string()
      .max(30)
      .required("error_field.last_name_error")
      .matches(checkEng, {
        message: "error_field.language_en_error",
        excludeEmptyString: true,
      }),
    firstNameTH: yup
      .string()
      .max(30)
      .matches(checkThai, {
        message: "error_field.language_th_error",
        excludeEmptyString: true,
      })
      .when("nationality", {
        is: (value?: Option) => value && value?.id === "Thai",
        then: (schema) => schema.required("error_field.first_name_error"),
      }),
    lastNameTH: yup
      .string()
      .max(30)
      .matches(checkThai, {
        message: "error_field.language_th_error",
        excludeEmptyString: true,
      })
      .when("nationality", {
        is: (value?: Option) => value && value?.id === "Thai",
        then: (schema) => schema.required("error_field.last_name_error"),
      }),
    dob: yup.lazy(() =>
      yup
        .date()
        .nullable()
        .required("error_field.dob_error")
        .when(["departureDate", "dob"], {
          is: (departureDate: Date | null, dob: Date | null) =>
            departureDate !== null &&
            dob !== null && // must be at least 6 months old on departure date
            dob <= subMonths(departureDate, 6) && // must be at most 80 years old on departure date
            dob >= subYears(departureDate, 80),
          then: (
            schema, // throw error if not in valid range
          ) =>
            schema.test("is-valid-age", "error_field.dob_invalid_error", () => {
              return true;
            }),
          otherwise: (schema) =>
            schema.test("is-valid-age", "error_field.dob_invalid_error", () => {
              return false;
            }),
        }),
    ),
    idCardNo: yup
      .string()
      .max(13, "error_field.id_card_no_digit_error")
      .matches(/\d{13}$/, {
        message: "error_field.id_card_no_digit_error",
        excludeEmptyString: true,
      })
      .when("nationality", {
        is: (value?: Option) => value && value?.id === "thai",
        then: (schema) => schema.required("error_field.id_card_no_error"),
      }),
    passportNo: yup.string().when("nationality", {
      is: (value?: Option) => value && value?.id === "other",
      then: (schema) => schema.required("error_field.nationality_error"),
    }),
    phoneNumber: yup
      .string()
      .matches(/\d{10}/, "error_field.phone_number_format_error")
      .max(10, "error_field.phone_number_format_error")
      .required("error_field.phone_number_error"),
    email: yup
      .string()
      .email("error_field.email_format_error")
      .required("error_field.email_error"),
    beneficiary: yup
      .object({
        id: yup.string().required("error_field.beneficiary_error"),
        label_th: yup.string(),
        label_en: yup.string(),
      })
      .required("error_field.beneficiary_error"),
    beneficiaryTitle: yup.object({
      id: yup.string().when("beneficiary", {
        is: (value?: Option) => value?.id === "specify",
        then: (schema) => schema.required("error_field.title_error"),
      }),
      label_th: yup.string(),
      label_en: yup.string(),
    }),
    beneficiaryFirstName: yup.lazy(() =>
      yup
        .string()
        .max(30)

        .when("beneficiary", {
          is: (value?: Option) => value?.id === "specify",
          then: (schema) =>
            schema
              .required("error_field.beneficiary_first_name_error")
              .test("Check Language", "error_field.language_error", (value) => {
                if (!value || value.length === 0) return false;
                return checkThai.test(value) || checkEng.test(value);
              }),
        }),
    ),
    beneficiaryLastName: yup.lazy(() =>
      yup
        .string()
        .max(30)

        .when("beneficiary", {
          is: (value?: Option) => value?.id === "specify",
          then: (schema) =>
            schema
              .required("error_field.beneficiary_last_name_error")
              .test("Check Language", "error_field.language_error", (value) => {
                if (!value || value.length === 0) return false;
                return checkThai.test(value) || checkEng.test(value);
              }),
        }),
    ),
    insuredAddress: yup.string().required("error_field.insured_address_error"),
    street: yup.string(),
    province: yup
      .object({
        province_id: yup.string().required("error_field.province_error"),
        province_name_th: yup.string(),
        province_name_en: yup.string(),
      })
      .test(
        "check valid",
        "error_field.province_error",
        (value) => value?.province_id !== undefined,
      )
      .required("error_field.province_error"),
    district: yup
      .object({
        district_id: yup.string().required("error_field.district_error"),
        province_id: yup.string(),
        district_name_th: yup.string(),
        district_name_en: yup.string(),
      })
      .test(
        "check valid",
        "error_field.district_error",
        (value) => value?.district_id !== undefined,
      )
      .required("error_field.district_error"),
    subDistrict: yup
      .object({
        subdistrict_id: yup.string().required("error_field.sub_district_error"),
        district_id: yup.string(),
        subdistrict_name_th: yup.string(),
        subdistrict_name_en: yup.string(),
      })
      .test(
        "check valid",
        "error_field.sub_district_error",
        (value) => value?.subdistrict_id !== undefined,
      )
      .required("error_field.sub_district_error"),
    postalCode: yup
      .object({
        postal_code: yup.string().required("error_field.postal_code_error"),
        subdistrict_id: yup.string(),
        district_id: yup.string(),
        province_id: yup.string(),
      })
      .test(
        "check valid",
        "error_field.postal_code_error",
        (value) => value?.postal_code !== undefined,
      )
      .required("error_field.postal_code_error"),
    promoCode: yup
      .string()
      .matches(checkNoSpecialChar, {
        message: "error_field.promocode_no_special_char_error",
        excludeEmptyString: true,
      })
      .required("error_field.promocode_error"),
    promoType: yup.string(),
    termConditionFlag: yup
      .boolean()
      .required("error_field.term_and_condition_error")
      .isTrue("error_field.term_and_condition_error"),
    marketingConsentFlag: yup.boolean(),
  });

  const { control, handleSubmit, watch, setValue, trigger, reset, resetField } =
    useForm<FormType>({
      mode: "all",
      resolver: yupResolver(schema),
      defaultValues: {
        nationality: nationality.data[0],
      },
    });

  const checkPromocodeValid = useCallback(
    async (promoCode: string): Promise<string> => {
      let isValid = false;
      let promoItems: PromoItem[] = [];
      let nextToken = null;
      let validResponse: AppResponse<PromoCodeResponse>;
      do {
        validResponse = await postGraphql<PromocodeRequest, PromoCodeResponse>(
          getPromocode,
          { code: promoCode, type: "PROMO_CODE" },
          nextToken,
        );

        if (validResponse.status !== API_STATUS.SUCCESS) {
          return "error_api.promocode_api_error";
        }

        promoItems = [
          ...promoItems,
          ...(validResponse.data?.getPromocode.items || []),
        ];
        isValid = promoItems.length > 0;
        nextToken = validResponse.data?.getPromocode.nextToken;
      } while (nextToken && !isValid);

      if (isValid) {
        let isExist = false;
        let webFormItems: { transactionId: string }[] = [];
        let nextToken = null;
        do {
          const response: AppResponse<WebFormByPromocodeResponse> =
            await postGraphql<string, WebFormByPromocodeResponse>(
              listWebFormByPromocode,
              promoCode,
              nextToken,
            );

          if (response.status !== API_STATUS.SUCCESS) {
            return "error_api.promocode_api_error";
          }

          webFormItems = [
            ...webFormItems,
            ...(response.data?.listWebFormByPromocode.items || []),
          ];

          isExist = webFormItems.length > 0;
          nextToken = response.data?.listWebFormByPromocode.nextToken;
        } while (nextToken && !isExist);

        if (isExist) {
          return "error_field.promocode_invalid";
        }

        // console.log("validResponse", validResponse.data);

        setValue(
          "promoType",
          JSON.parse(validResponse.data?.getPromocode.items[0].data || "{}")
            ?.partner || "",
        );

        return "";
      }

      return "error_field.promocode_invalid";
    },
    [setValue],
  );

  const checkDuplicateSubmit = useCallback(async (): Promise<string> => {
    let nextToken = null;
    let isDuplicate = false;
    do {
      const response: AppResponse<WebFormByRequesterResponse> =
        await postGraphql<
          WebFormByRequesterRequest,
          WebFormByRequesterResponse
        >(
          listWebFormByRequester,
          {
            idCardNo: watch("idCardNo"),
            passportNo: watch("passportNo")?.toUpperCase(),
          },
          nextToken,
        );

      if (response.status !== API_STATUS.SUCCESS) {
        return "error_api.something_went_wrong";
      }

      const items = response.data?.listWebFormByRequester.items;
      if (items && items.length > 0) {
        for (const item of items) {
          const data = JSON.parse(item.data);
          if (
            data.destination?.id === watch("destination")?.id &&
            data.departureDate?.slice(0, 10) ===
              watch("departureDate")?.toISOString().slice(0, 10) &&
            data.returnDate?.slice(0, 10) ===
              watch("returnDate")?.toISOString().slice(0, 10)
          ) {
            isDuplicate = true;

            return "error_field.data_duplicate";
          } else {
            isDuplicate = false;
          }
        }
      }

      nextToken = response.data?.listWebFormByRequester.nextToken;
    } while (nextToken && !isDuplicate);

    return isDuplicate ? "error_field.data_duplicate" : "";
  }, [watch]);

  const handleOnSubmit = useCallback(
    async (data: FormType) => {
      // console.log("submit data", data);

      const response = await postGraphql<WebFormRequest, WebFormResponse>(
        createWebForm,
        {
          type: WebFormType.SPECIAL_GIVEAWAY,
          data: {
            destination: data.destination,
            departureDate: data.departureDate?.toISOString() || "",
            returnDate: data.returnDate?.toISOString() || "",
            travelDays: data.travelDays || 0,
            coveragePlan: CoveragePlan.GOLD,
            nationality: nationality.data.find(
              (national) => national.id === data.nationality.id,
            ),
            title: title.data.find(({ id }) => id === data.title.id),
            firstName: data.firstName,
            lastName: data.lastName,
            firstNameTH: data.firstNameTH || "",
            lastNameTH: data.lastNameTH || "",
            dob: data.dob?.toISOString() || "",
            age: data.dob ? calculateAgeInYearsMonths(data.dob) : 0,
            idCardNo: data.nationality.id === "Thai" ? data.idCardNo || "" : "",
            passportNo:
              data.nationality.id !== "Thai"
                ? data.passportNo?.toUpperCase() || ""
                : "",
            phoneNumber: data.phoneNumber,
            email: data.email,
            beneficiary: beneficiary.data.find(
              (beneficiary) => beneficiary.id === data.beneficiary.id,
            ),
            beneficiaryTitle: title.data.find(
              ({ id }) => id === data.beneficiaryTitle?.id,
            ),
            beneficiaryFirstName: data.beneficiaryFirstName || "",
            beneficiaryLastName: data.beneficiaryLastName || "",
            insuredAddress: data.insuredAddress,
            street: data.street || "",
            province: data.province,
            district: data.district,
            subDistrict: data.subDistrict,
            postalCode: data.postalCode,
            promoCode: data.promoCode || "",
            partner: data.promoType || "",
            termConditionFlag: data.termConditionFlag,
            marketingConsentFlag: data.marketingConsentFlag || false,
            receivedDateTime: new Date().toISOString(),
          },
        },
      );

      setIsLoading(false);
      // console.log("response", response);

      if (response.status === API_STATUS.SUCCESS) {
        onChangeStep(2);
      } else {
        setErrorText(t(response.message || "submit_error"));
      }
    },
    [onChangeStep, t],
  );

  const handleOnClickClear = useCallback(() => {
    reset(
      {
        nationality: nationality.data[0],
      },
      {
        keepErrors: false,
        keepValues: false,
        keepDefaultValues: true,
      },
    );

    setErrorText("");
    setRefresh(true);
  }, [reset]);

  const handleOnClickSend = useCallback(async () => {
    setIsLoading(true);
    const isValidForm = await trigger(undefined, { shouldFocus: true });
    const checkValid = await checkPromocodeValid(watch("promoCode"));
    // console.log("check field errors", JSON.stringify(errors));

    if (
      isValidForm &&
      promoCodeValidError.length === 0 &&
      checkValid.length === 0
    ) {
      const checkDuplicate = await checkDuplicateSubmit();

      if (checkDuplicate.length > 0) {
        setErrorText(checkDuplicate);
        setIsLoading(false);
        return;
      } else {
        formRef.current?.requestSubmit();
      }
    } else if (checkValid.length > 0) {
      setPromoCodeValidError(checkValid);
      setIsLoading(false);
      return;
    } else if (!isValidForm) {
      setErrorText("error_form.form_invalid");
      setIsLoading(false);
      return;
    } else {
      setIsLoading(false);
      return;
    }
  }, [
    checkDuplicateSubmit,
    checkPromocodeValid,
    promoCodeValidError.length,
    trigger,
    watch,
  ]);

  const handleOpenLinkPdpa = useCallback(() => {
    if (i18n.language === "th") {
      window.open(
        "https://generali.co.th/insurance-application-pdpa-consent/",
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        "https://generali.co.th/en/insurance-application-pdpa-consent/",
        "_blank",
        "noopener,noreferrer",
      );
    }
  }, [i18n.language]);

  const handleOpenLinkTC = useCallback(() => {
    if (i18n.language === "th") {
      window.open(
        "https://generali.co.th/travel-insurance-terms-conditions/",
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        "https://generali.co.th/en/travel-insurance-terms-conditions/",
        "_blank",
        "noopener,noreferrer",
      );
    }
  }, [i18n.language]);

  const handleOpenLinkMarketing = useCallback(() => {
    if (i18n.language === "th") {
      window.open(
        "https://generali.co.th/marketingconsent/",
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        "https://generali.co.th/en/marketingconsent/",
        "_blank",
        "noopener,noreferrer",
      );
    }
  }, [i18n.language]);

  const handleOpenLinkMoreDetail = useCallback(() => {
    if (i18n.language === "th") {
      window.open(
        "https://generali.co.th/travel-special-giveaway/",
        "_blank",
        "noopener,noreferrer",
      );
    } else {
      window.open(
        "https://generali.co.th/en/travel-special-giveaway/",
        "_blank",
        "noopener,noreferrer",
      );
    }
  }, [i18n.language]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
    }
  }, [refresh]);

  return !refresh ? (
    <>
      <GenLayout>
        <Box
          borderRadius="0.75rem"
          paddingY="1.75rem"
          paddingX={isMobile ? "0.5rem" : "3rem"}
          sx={{ backgroundColor: "white" }}
        >
          <Typography variant="h1" marginBottom={2} textAlign="center">
            {t("form_header")}
          </Typography>
          <Typography
            variant="h3"
            marginBottom={2}
            textAlign="center"
            whiteSpace={"pre"}
            sx={{
              fontWeight: "bold",
              color: "black",
            }}
          >
            {t("form_description1")}
          </Typography>
          <Typography
            variant="h3"
            marginBottom={2}
            textAlign="center"
            whiteSpace={"pre"}
            sx={{
              fontWeight: "normal",
            }}
          >
            {t("form_description2")}
          </Typography>
          <form ref={formRef} onSubmit={handleSubmit(handleOnSubmit)}>
            <Box display="flex" flexDirection="column" gap={1} marginTop={4}>
              <Grid>
                <Grid marginBottom={1}>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.travel_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 20 : 25}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="destination"
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<Country>
                        {...field}
                        required
                        disabled={isLoading}
                        label={t("field_name.destination")}
                        error={fieldState.error?.message}
                        data={countries.data}
                        labelAttractor={(data) =>
                          i18n.language === "th" ? data.label_th : data.label_en
                        }
                        filterOptions={(items, value = "") =>
                          items.filter(
                            (item) =>
                              item.label_th.includes(value) ||
                              item.label_en
                                .toLowerCase()
                                .includes(value.toLowerCase()),
                          )
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid
                display="flex"
                flexDirection="column"
                justifyContent="stretch"
                gap={2}
              >
                <Grid>
                  <Controller
                    control={control}
                    name="departureDate"
                    render={({ field, fieldState }) => (
                      <GenDatePicker
                        {...field}
                        required
                        label={t("field_name.departure_date")}
                        minDate={addDays(new Date(), 7)}
                        maxDate={addDays(new Date(), 180)}
                        disabled={isLoading}
                        error={fieldState.error?.message}
                        onChange={(value) => {
                          resetField("returnDate", { defaultValue: null });
                          resetField("travelDays", { defaultValue: 0 });
                          setRefresh(true);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="returnDate"
                    render={({ field, fieldState }) => (
                      <GenDatePicker
                        {...field}
                        required
                        label={t("field_name.return_date")}
                        minDate={
                          watch("departureDate") || addDays(new Date(), 1)
                        }
                        maxDate={addDays(
                          watch("departureDate") || new Date(),
                          6,
                        )}
                        disabled={!watch("departureDate") || isLoading}
                        placeholder={t("field_placeholder.return_date")}
                        error={fieldState.error?.message}
                        onChange={(value) => {
                          const travelDays =
                            value !== null && watch("departureDate")
                              ? differenceInCalendarDays(
                                  value,
                                  watch("departureDate") ||
                                    addDays(new Date(), 1),
                                ) + 1
                              : 0;

                          setValue("travelDays", travelDays);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="travelDays"
                    render={({ field, fieldState }) => (
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap={1}
                        justifyItems="center"
                        marginY={1}
                      >
                        <GenTextField
                          {...field}
                          fullWidth
                          disabled={true}
                          label={
                            typeof field.value !== "number"
                              ? t("field_name.travel_days")
                              : ""
                          }
                          sx={{
                            flexGrow: 1,
                          }}
                          onChange={() => {}}
                        />
                        <Typography
                          component="div"
                          alignContent="center"
                          justifyContent="right"
                        >
                          {t("field_suffix.travel_days")}
                        </Typography>
                      </Box>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid marginTop={2} marginBottom={2}>
                <Grid>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.personal_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 19 : 27}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="nationality"
                    defaultValue={nationality.data[0]}
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<Option>
                        {...field}
                        required
                        disabled={isLoading}
                        label={t("field_name.nationality")}
                        error={fieldState.error?.message}
                        data={nationality.data}
                        labelAttractor={(data) =>
                          i18n.language === "th" ? data.label_th : data.label_en
                        }
                        filterOptions={(items, value = "") =>
                          items.filter(
                            (item) =>
                              item.label_th.includes(value) ||
                              item.label_en
                                .toLowerCase()
                                .includes(value.toLowerCase()),
                          )
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  {watch("nationality")?.id !== "Thai" ? (
                    <Controller
                      control={control}
                      name="passportNo"
                      render={({ field, fieldState }) => (
                        <GenTextField
                          {...field}
                          fullWidth
                          disabled={isLoading}
                          required
                          error={t(fieldState.error?.message || "")}
                          label={t("field_name.passport_no")}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="idCardNo"
                      render={({ field, fieldState }) => (
                        <GenTextField
                          fullWidth
                          {...field}
                          disabled={isLoading}
                          required
                          error={t(fieldState.error?.message || "")}
                          label={t("field_name.id_card_no")}
                        />
                      )}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid direction="column">
                <Grid>
                  <Controller
                    control={control}
                    name="title.id"
                    render={({ field, fieldState }) => (
                      <GenDropDown
                        {...field}
                        required
                        label={t("field_name.title")}
                        labelId="title"
                        error={fieldState.error?.message}
                        items={title.data.map((value) => ({
                          value: value.id,
                          label:
                            i18n.language === "th"
                              ? value.label_th
                              : value.label_en,
                        }))}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="firstName"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        disabled={isLoading}
                        required
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.first_name")}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="lastName"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        required
                        disabled={isLoading}
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.last_name")}
                      />
                    )}
                  />
                </Grid>
                {watch("nationality")?.id === "Thai" ? (
                  <>
                    <Grid>
                      <Controller
                        control={control}
                        name="firstNameTH"
                        render={({ field, fieldState }) => (
                          <GenTextField
                            {...field}
                            fullWidth
                            disabled={isLoading}
                            required
                            error={t(fieldState.error?.message || "")}
                            label={t("field_name.first_name_th")}
                          />
                        )}
                      />
                    </Grid>
                    <Grid>
                      <Controller
                        control={control}
                        name="lastNameTH"
                        render={({ field, fieldState }) => (
                          <GenTextField
                            {...field}
                            fullWidth
                            required
                            disabled={isLoading}
                            error={t(fieldState.error?.message || "")}
                            label={t("field_name.last_name_th")}
                          />
                        )}
                      />
                    </Grid>
                  </>
                ) : null}
                <Grid marginTop={1}>
                  <Controller
                    control={control}
                    name="dob"
                    render={({ field, fieldState }) => (
                      <GenDatePicker
                        {...field}
                        required
                        label={t("field_name.dob")}
                        minDate={subYears(
                          watch("departureDate") || addDays(new Date(), 1),
                          80,
                        )}
                        maxDate={subMonths(
                          watch("departureDate") || addDays(new Date(), 1),
                          6,
                        )}
                        disabled={isLoading}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid marginTop={2} marginBottom={1}>
                <Grid>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.contact_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 19 : 27}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        required
                        disabled={isLoading}
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.phone_number")}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        required
                        type="email"
                        disabled={isLoading}
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.email")}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid marginTop={2} marginBottom={2}>
                <Grid>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.beneficiary_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 22 : 30}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="beneficiary.id"
                    render={({ field, fieldState }) => (
                      <GenDropDown
                        {...field}
                        required
                        label={t("field_name.beneficiary")}
                        labelId="beneficiary"
                        error={fieldState.error?.message}
                        items={beneficiary.data.map((value) => ({
                          value: value.id,
                          label:
                            i18n.language === "th"
                              ? value.label_th
                              : value.label_en,
                        }))}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {watch("beneficiary.id") &&
              watch("beneficiary.id") !== "Insured Estate" ? (
                <Grid gap={2}>
                  <Grid>
                    <Controller
                      control={control}
                      name="beneficiaryTitle.id"
                      render={({ field, fieldState }) => (
                        <GenDropDown
                          {...field}
                          label={t("field_name.title")}
                          labelId="beneficiaryTitle"
                          required
                          error={fieldState.error?.message}
                          items={title.data.map((value) => ({
                            value: value.id,
                            label:
                              i18n.language === "th"
                                ? value.label_th
                                : value.label_en,
                          }))}
                        />
                      )}
                    />
                  </Grid>
                  <Grid>
                    <Controller
                      control={control}
                      name="beneficiaryFirstName"
                      render={({ field, fieldState }) => (
                        <GenTextField
                          {...field}
                          fullWidth
                          disabled={isLoading}
                          required
                          error={t(fieldState.error?.message || "")}
                          label={t("field_name.beneficiary_first_name")}
                        />
                      )}
                    />
                  </Grid>
                  <Grid>
                    <Controller
                      control={control}
                      name="beneficiaryLastName"
                      render={({ field, fieldState }) => (
                        <GenTextField
                          {...field}
                          fullWidth
                          required
                          disabled={isLoading}
                          error={t(fieldState.error?.message || "")}
                          label={t("field_name.beneficiary_last_name")}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              ) : null}
              <Grid marginTop={2} marginBottom={1}>
                <Grid>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.insured_address_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 25 : 24}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="insuredAddress"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        required
                        disabled={isLoading}
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.insured_address")}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="street"
                    render={({ field, fieldState }) => (
                      <GenTextField
                        {...field}
                        fullWidth
                        disabled={isLoading}
                        error={t(fieldState.error?.message || "")}
                        label={t("field_name.street")}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="province"
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<Province>
                        {...field}
                        required
                        disabled={isLoading}
                        label={t("field_name.province")}
                        error={fieldState.error?.message}
                        data={provinces.data}
                        labelAttractor={(data) =>
                          i18n.language === "th"
                            ? data.province_name_th
                            : data.province_name_en
                        }
                        filterOptions={(items, value = "") =>
                          items.filter(
                            (item) =>
                              item.province_name_th.includes(value) ||
                              item.province_name_en
                                .toLowerCase()
                                .includes(value.toLowerCase()),
                          )
                        }
                        onChange={(value) => {
                          resetField("district");
                          resetField("subDistrict");
                          resetField("postalCode");

                          if (!Array.isArray(value)) {
                            const districtList = allDistricts.data.filter(
                              (data) => data.province_id === value?.province_id,
                            );
                            setDistricts(districtList);
                          }

                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="district"
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<District>
                        {...field}
                        required
                        disabled={isLoading || districts.length === 0}
                        label={t("field_name.district")}
                        error={fieldState.error?.message}
                        data={districts}
                        labelAttractor={(data) =>
                          i18n.language === "th"
                            ? data.district_name_th
                            : data.district_name_en
                        }
                        filterOptions={(items, value = "") =>
                          items.filter(
                            (item) =>
                              item.district_name_th.includes(value) ||
                              item.district_name_en
                                .toLowerCase()
                                .includes(value.toLowerCase()),
                          )
                        }
                        onChange={(value) => {
                          resetField("subDistrict");
                          resetField("postalCode");

                          if (!Array.isArray(value)) {
                            const subDistrictList = allSubDistricts.data.filter(
                              (data) =>
                                data.district_id === value?.district_id &&
                                data.province_id === value.province_id,
                            );
                            setSubDistricts(subDistrictList);
                          }

                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="subDistrict"
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<SubDistrict>
                        {...field}
                        required
                        disabled={isLoading || districts.length === 0}
                        label={t("field_name.sub_district")}
                        error={fieldState.error?.message}
                        data={subDistricts}
                        labelAttractor={(data) =>
                          i18n.language === "th"
                            ? data.subdistrict_name_th
                            : data.subdistrict_name_en
                        }
                        filterOptions={(items, value = "") =>
                          items.filter(
                            (item) =>
                              item.subdistrict_name_th.includes(value) ||
                              item.subdistrict_name_en
                                .toLowerCase()
                                .includes(value.toLowerCase()),
                          )
                        }
                        onChange={(value) => {
                          resetField("postalCode");

                          if (!Array.isArray(value)) {
                            const postalCodeList = allPostalCodes.data.filter(
                              (data) =>
                                data.subdistrict_id === value?.subdistrict_id &&
                                data.district_id === value?.district_id,
                            );
                            setPostalCodes(postalCodeList);

                            if (postalCodeList.length === 1) {
                              setValue("postalCode", postalCodeList[0]);
                            }
                          }

                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="postalCode"
                    render={({ field, fieldState }) => (
                      <GenAutoComplete<PostalCode>
                        {...field}
                        required
                        disabled={isLoading || districts.length === 0}
                        label={t("field_name.postal_code")}
                        error={fieldState.error?.message}
                        data={postalCodes}
                        labelAttractor={(data) => data.postal_code}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid marginTop={1.8} marginBottom={1}>
                <Grid>
                  <Box
                    width={20}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                  <Box sx={{ marginTop: -1, paddingLeft: 4 }}>
                    <Typography variant="h2">
                      {t("field_section.promocode_info")}
                    </Typography>
                  </Box>
                  <Box
                    marginTop={-1.5}
                    marginLeft={i18n.language === "th" ? 21 : 17}
                    borderTop={`1px solid ${SECONDARY_COLOR_LIGHT}`}
                  ></Box>
                </Grid>
              </Grid>
              <Grid>
                <Grid marginBottom={2}>
                  <Controller
                    control={control}
                    name="promoCode"
                    render={({ field, fieldState }) => (
                      <>
                        <GenTextField
                          {...field}
                          fullWidth
                          required
                          error={t(
                            fieldState.error?.message || promoCodeValidError,
                          )}
                          disabled={isLoading}
                          label={t("field_name.promocode")}
                          onBlur={async (e) => {
                            const value = e.target?.value || "";
                            setValue("promoType", "");

                            if (value.length > 0) {
                              const validPromoError =
                                await checkPromocodeValid(value);
                              setPromoCodeValidError(validPromoError);
                            }

                            field.onBlur();
                          }}
                        />
                      </>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid gap={2}>
                <Grid marginBottom={2}>
                  <Controller
                    control={control}
                    name="termConditionFlag"
                    render={({ field, fieldState }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox {...field} />}
                          label={
                            <Typography>
                              {t("field_name.term_and_condition_1")}
                              <GenButton
                                component={"a"}
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  textTransform: "none",
                                }}
                                variant="text"
                                onClick={handleOpenLinkPdpa}
                              >
                                {t("field_name.term_and_condition_link_pp")}
                              </GenButton>
                              {t("field_name.term_and_condition_2")}
                              <GenButton
                                component={"a"}
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  textTransform: "none",
                                }}
                                variant="text"
                                onClick={handleOpenLinkTC}
                              >
                                {t("field_name.term_and_condition_link_tc")}
                              </GenButton>
                            </Typography>
                          }
                        />
                        {fieldState.error?.message?.length ? (
                          <FormHelperText
                            sx={{
                              marginLeft: 4,
                              marginTop: 1,
                              marginBottom: 2,
                            }}
                          >
                            <Typography variant="caption" color="error">
                              {t(fieldState.error?.message)}
                            </Typography>
                          </FormHelperText>
                        ) : null}
                      </FormGroup>
                    )}
                  />
                </Grid>
                <Grid>
                  <Controller
                    control={control}
                    name="marketingConsentFlag"
                    render={({ field, fieldState }) => (
                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox {...field} />}
                          label={
                            <Typography>
                              {t("field_name.marketing_consent")}
                              <GenButton
                                component={"a"}
                                sx={{
                                  padding: 0,
                                  margin: 0,
                                  width: "fit-content",
                                  marginLeft: i18n.language === "th" ? -2.5 : 0,
                                  textTransform: "none",
                                }}
                                variant="text"
                                onClick={handleOpenLinkMarketing}
                              >
                                {t("field_name.marketing_consent_link")}
                              </GenButton>
                            </Typography>
                          }
                        />
                        {fieldState.error?.message?.length ? (
                          <FormHelperText>
                            <Typography
                              marginTop={1}
                              variant="caption"
                              color="error"
                            >
                              {t(fieldState.error?.message)}
                            </Typography>
                          </FormHelperText>
                        ) : null}
                      </FormGroup>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid>
                <Grid marginTop={1} marginBottom={1}>
                  <Typography fontStyle="italic">
                    {t("more_detail")}
                    <GenButton
                      component="a"
                      sx={{
                        padding: 0,
                        margin: 0,
                        textTransform: "none",
                        marginLeft: i18n.language === "th" ? -2.5 : -1.5,
                      }}
                      variant="text"
                      onClick={handleOpenLinkMoreDetail}
                    >
                      {t("member_link")}
                    </GenButton>
                  </Typography>
                </Grid>
              </Grid>
              <Grid
                display="flex"
                flexDirection="column-reverse"
                gap={2}
                justifyContent="stretch"
                alignContent="center"
                marginTop={4}
              >
                <GenButton
                  disabled={isLoading}
                  onClick={handleOnClickClear}
                  variant="outlined"
                  color="secondary"
                >
                  {t("clear")}
                </GenButton>
                <GenButton
                  onClick={handleOnClickSend}
                  variant="contained"
                  color="primary"
                  loading={isLoading}
                >
                  {t("submit_request")}
                </GenButton>
                {errorText.length > 0 ? (
                  <Typography color="error" textAlign="center">
                    {t(errorText)}
                  </Typography>
                ) : null}
              </Grid>
            </Box>
          </form>
        </Box>
      </GenLayout>
    </>
  ) : null;
};

export default GiveAway;
